import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

interface SafeAreaBottomProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  /** Extra padding added on top of the safe-area inset (default 0). */
  extra?: number | string
}

/**
 * Adds `padding-bottom: env(safe-area-inset-bottom)` so content is never
 * obscured by the iOS home indicator. Use wherever a scroll container or
 * fixed element needs to clear the notch.
 *
 * Why: Chrome DevTools responsive mode does NOT simulate safe-area insets.
 * Rolling your own `paddingBottom` calculation leads to regressing on real
 * iOS Safari (see FOR-1544, FOR-1584, FOR-1597). Compose this instead.
 */
export function SafeAreaBottom({ children, extra = 0, style, ...props }: SafeAreaBottomProps) {
  const extraStr = typeof extra === 'number' ? `${extra}px` : extra
  const paddingBottom: CSSProperties['paddingBottom'] =
    extra === 0
      ? 'env(safe-area-inset-bottom, 0px)'
      : `calc(env(safe-area-inset-bottom, 0px) + ${extraStr})`

  return (
    <div style={{ paddingBottom, ...style }} {...props}>
      {children}
    </div>
  )
}
